import React, { useState } from 'react';
import { Mail, Clock, CheckCircle2, MessageCircle, MessageSquare, X, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import axiosClient from '../../api/axiosClient';
import EnquiryChatModal from './EnquiryChatModal';

export default function EnquiriesTable({ enquiries, refreshEnquiries }) {
  const [respondModal, setRespondModal] = useState({ isOpen: false, enquiry: null });
  const [viewModal, setViewModal] = useState({ isOpen: false, enquiry: null });
  const [chatModal, setChatModal] = useState({ isOpen: false, enquiry: null });
  const [responseMessage, setResponseMessage] = useState('');
  const [isSending, setIsSending] = useState(false);

  const handleRespond = async (e) => {
    e.preventDefault();
    if (!respondModal.enquiry || !responseMessage) return;
    
    setIsSending(true);
    try {
      await axiosClient.post(`/admin/enquiries/${respondModal.enquiry.id}/respond`, { message: responseMessage });
      setRespondModal({ isOpen: false, enquiry: null });
      setResponseMessage('');
      if (refreshEnquiries) refreshEnquiries();
      toast.success('Response sent successfully');
    } catch (err) {
      console.error('Failed to send response', err);
      toast.error('Failed to send response');
    } finally {
      setIsSending(false);
    }
  };
  if (!enquiries || enquiries.length === 0) {
    return (
      <div className="p-8 text-center border-t border-slate-100 dark:border-slate-800">
        <p className="text-slate-500 dark:text-slate-400">No enquiries found.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="bg-slate-50 dark:bg-[#0b1220] border-b border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 text-sm uppercase tracking-wider">
            <th className="p-4 font-medium">Name</th>
            <th className="p-4 font-medium">Contact</th>
            <th className="p-4 font-medium">Subject</th>
            <th className="p-4 font-medium">Date</th>
            <th className="p-4 font-medium">Status</th>
            <th className="p-4 font-medium text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {enquiries.map((enq) => (
            <tr key={enq.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
              <td className="p-4 align-top">
                <div className="font-medium text-slate-900 dark:text-white">{enq.first_name} {enq.last_name}</div>
                {enq.school_name && <div className="text-sm text-slate-500 dark:text-slate-400">{enq.school_name}</div>}
              </td>
              <td className="p-4 align-top text-sm">
                <div className="text-slate-900 dark:text-white">{enq.email}</div>
                {enq.phone && <div className="text-slate-500 dark:text-slate-400">{enq.phone}</div>}
              </td>
              <td className="p-4 align-top font-medium text-slate-900 dark:text-white">
                {enq.subject}
              </td>
              <td className="p-4 align-top text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">
                {enq.created_at ? new Date(enq.created_at).toLocaleDateString() : 'N/A'}
              </td>
              <td className="p-4 align-top">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                  enq.status === 'unread' ? 'bg-amber-100 text-amber-700' :
                  enq.status === 'read' ? 'bg-blue-100 text-blue-700' :
                  'bg-emerald-100 text-emerald-700 dark:text-emerald-400'
                }`}>
                  {enq.status === 'unread' ? <Clock size={12} /> : enq.status === 'read' ? <Mail size={12} /> : <CheckCircle2 size={12} />}
                  <span className="capitalize">{enq.status}</span>
                </span>
              </td>
              <td className="p-4 align-top text-right">
                <div className="flex items-center justify-end gap-2">
                  <button 
                    onClick={() => setViewModal({ isOpen: true, enquiry: enq })}
                    className="btn btn-outline py-1 px-3 text-sm"
                  >
                    View More
                  </button>
                  <button 
                    onClick={() => setChatModal({ isOpen: true, enquiry: enq })}
                    className="btn btn-outline py-1 px-3 text-sm flex items-center gap-1.5"
                    title="Chat / Send Attachments"
                  >
                    <MessageSquare size={14} /> Chat
                  </button>
                  <button 
                    onClick={() => setRespondModal({ isOpen: true, enquiry: enq })}
                    disabled={enq.status === 'read' || enq.status === 'resolved'}
                    className="btn py-1 px-3 text-sm flex items-center gap-1.5 bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <MessageCircle size={14} /> Respond
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Respond Modal */}
      {respondModal.isOpen && respondModal.enquiry && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white dark:bg-[#1e2336] rounded-2xl shadow-xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-[#0b1220]">
              <h3 className="font-bold text-slate-900 dark:text-white">Respond to Enquiry</h3>
              <button onClick={() => setRespondModal({ isOpen: false, enquiry: null })} className="text-slate-400 hover:text-slate-600 dark:text-slate-300">
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              <div className="bg-slate-50 dark:bg-[#0b1220] rounded-xl p-4 mb-6 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Name:</span>
                  <span className="text-sm text-slate-900 dark:text-white">{respondModal.enquiry.first_name} {respondModal.enquiry.last_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Email:</span>
                  <span className="text-sm text-slate-900 dark:text-white">{respondModal.enquiry.email}</span>
                </div>
                {respondModal.enquiry.school_name && (
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">School:</span>
                    <span className="text-sm text-slate-900 dark:text-white">{respondModal.enquiry.school_name}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Subject:</span>
                  <span className="text-sm text-slate-900 dark:text-white">{respondModal.enquiry.subject}</span>
                </div>
              </div>
              
              <form onSubmit={handleRespond}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Your Response</label>
                  <textarea 
                    rows="5" 
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-shadow"
                    placeholder="Type your response here..."
                    value={responseMessage}
                    onChange={(e) => setResponseMessage(e.target.value)}
                    required
                  ></textarea>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button type="button" onClick={() => setRespondModal({ isOpen: false, enquiry: null })} className="btn btn-outline py-2 px-4">
                    Cancel
                  </button>
                  <button type="submit" disabled={isSending} className="btn btn-primary py-2 px-4 flex items-center gap-2">
                    {isSending ? 'Sending...' : 'Send Response'} <Send size={16} />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View More Modal */}
      {viewModal.isOpen && viewModal.enquiry && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white dark:bg-[#1e2336] rounded-2xl shadow-xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-[#0b1220]">
              <h3 className="font-bold text-slate-900 dark:text-white">Enquiry Details</h3>
              <button onClick={() => setViewModal({ isOpen: false, enquiry: null })} className="text-slate-400 hover:text-slate-600 dark:text-slate-300">
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              <div className="bg-slate-50 dark:bg-[#0b1220] rounded-xl p-4 mb-6 space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Name:</span>
                  <span className="text-sm text-slate-900 dark:text-white">{viewModal.enquiry.first_name} {viewModal.enquiry.last_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Email:</span>
                  <span className="text-sm text-slate-900 dark:text-white">{viewModal.enquiry.email}</span>
                </div>
                {viewModal.enquiry.school_name && (
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">School:</span>
                    <span className="text-sm text-slate-900 dark:text-white">{viewModal.enquiry.school_name}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Date:</span>
                  <span className="text-sm text-slate-900 dark:text-white">{viewModal.enquiry.created_at ? new Date(viewModal.enquiry.created_at).toLocaleString() : 'N/A'}</span>
                </div>
              </div>
              
              <div className="mb-6">
                <h4 className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">Subject</h4>
                <p className="text-slate-900 dark:text-white font-medium bg-slate-50 dark:bg-[#0b1220] p-3 rounded-lg border border-slate-100 dark:border-slate-800">{viewModal.enquiry.subject}</p>
              </div>

              <div>
                <h4 className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">Message</h4>
                <div className="bg-slate-50 dark:bg-[#0b1220] p-4 rounded-lg border border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200 whitespace-pre-wrap">
                  {viewModal.enquiry.message ?? "No message"}
                </div>
              </div>

              {viewModal.enquiry.response && (
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">Admin Response</h4>
                  <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-lg border border-emerald-100 dark:border-emerald-800/50 text-emerald-800 whitespace-pre-wrap">
                    {viewModal.enquiry.response}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 mt-8">
                <button type="button" onClick={() => setViewModal({ isOpen: false, enquiry: null })} className="btn btn-primary py-2 px-6">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chat Modal */}
      <EnquiryChatModal 
        isOpen={chatModal.isOpen} 
        onClose={() => setChatModal({ isOpen: false, enquiry: null })} 
        enquiry={chatModal.enquiry} 
      />
    </div>
  );
}
